import { AccessGate } from '../../../shared/access/AccessControls';
import { useAllowedMutation } from '../../../shared/access/AccessControls';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
  VStack,
} from '@chakra-ui/react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format, parseISO } from 'date-fns';
import { dashboardShareAdminApi } from '../../../store/services/DashboardShareAdminService';
import {
  CreatedShareToken,
  DashboardShareTokenRow,
  DashboardShareTokenStatus,
} from '../../../store/responseTypes/dashboardShareTokens';

const TABULAR = { fontVariantNumeric: 'tabular-nums' as const };

const STATUS_COLOR: Record<DashboardShareTokenStatus, string> = {
  active: 'green.600',
  revoked: 'gray.500',
  expired: 'gray.500',
};

const fmtDate = (iso: string | null): string => {
  if (!iso) return '—';
  try {
    return format(parseISO(iso), 'd MMM yyyy');
  } catch {
    return '—';
  }
};

const startOfTomorrow = (): Date => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 1);
  return d;
};

const defaultExpiry = (): Date => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setFullYear(d.getFullYear() + 1);
  return d;
};

const maxExpiry = (): Date => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setFullYear(d.getFullYear() + 5);
  return d;
};

const buildShareUrl = (token: string): string =>
  `${window.location.origin}/share/dashboard#${token}`;

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CompaniesStatsShareModal: React.FC<ShareModalProps> = ({
  isOpen,
  onClose,
}) => {
  const toast = useToast();
  const { data, isLoading, isError } =
    dashboardShareAdminApi.useListDashboardShareTokensQuery(undefined, {
      skip: !isOpen,
    });
  const [createMutation, { isLoading: isCreating }] =
    useAllowedMutation(dashboardShareAdminApi.useCreateDashboardShareTokenMutation, 'createDashboardShareToken');
  const [revokeMutation] =
    useAllowedMutation(dashboardShareAdminApi.useRevokeDashboardShareTokenMutation, 'revokeDashboardShareToken');

  const [label, setLabel] = useState('');
  const [expiry, setExpiry] = useState<Date | null>(defaultExpiry());
  const [createdToken, setCreatedToken] = useState<CreatedShareToken | null>(
    null,
  );
  const [pendingRevokeId, setPendingRevokeId] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setLabel('');
      setExpiry(defaultExpiry());
      setCreatedToken(null);
      setPendingRevokeId(null);
    }
  }, [isOpen]);

  const rows: DashboardShareTokenRow[] = useMemo(
    () =>
      data?.items
        ? [...data.items].sort((a, b) => b.createdAt.localeCompare(a.createdAt))
        : [],
    [data],
  );

  const trimmedLabel = label.trim();
  const tomorrow = startOfTomorrow();
  const max = maxExpiry();
  const expiryValid =
    expiry !== null && expiry.getTime() >= tomorrow.getTime() &&
    expiry.getTime() <= max.getTime();
  const canSubmit = trimmedLabel.length > 0 && expiryValid && !isCreating;

  const handleCreate = async () => {
    if (!canSubmit || !expiry) return;
    try {
      const created = await createMutation({
        data: {
          label: trimmedLabel,
          expiresAt: expiry.toISOString(),
        },
      }).unwrap();
      setCreatedToken(created);
      setLabel('');
      setExpiry(defaultExpiry());
    } catch {
      toast({
        title: 'Couldn’t create share link.',
        description: 'The server returned an error. Try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    }
  };

  const handleRevoke = async (tokenId: string) => {
    if (pendingRevokeId === tokenId) {
      try {
        await revokeMutation({ tokenId }).unwrap();
        setPendingRevokeId(null);
      } catch {
        setPendingRevokeId(null);
        toast({
          title: 'Couldn’t revoke share link.',
          description: 'The server returned an error. Try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
      }
      return;
    }
    setPendingRevokeId(tokenId);
  };

  const handleCopy = async () => {
    if (!createdToken) return;
    const url = buildShareUrl(createdToken.token);
    try {
      await navigator.clipboard.writeText(url);
      toast({
        title: 'Link copied.',
        status: 'success',
        duration: 2000,
        isClosable: true,
        position: 'top-right',
      });
    } catch {
      toast({
        title: 'Couldn’t copy.',
        description: 'Select the URL above and copy manually.',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'top-right',
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent mx="1rem">
        <ModalHeader
          fontWeight={500}
          letterSpacing="-0.01em"
          color="gray.900"
        >
          Share links
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <VStack align="stretch" spacing={8}>
            <Box>
              <Text
                fontSize="xs"
                letterSpacing="0.2em"
                textTransform="uppercase"
                color="gray.400"
                fontWeight={500}
                mb={3}
              >
                Existing links
              </Text>
              {isLoading ? (
                <HStack justify="center" py={8}>
                  <Spinner size="sm" thickness="2px" color="gray.400" />
                </HStack>
              ) : isError ? (
                <Text fontSize="sm" color="red.500" py={4}>
                  Couldn’t load existing share links.
                </Text>
              ) : rows.length === 0 ? (
                <Text fontSize="sm" color="gray.500" py={4}>
                  No share links yet.
                </Text>
              ) : (
                <Box overflowX="auto">
                  <Table size="sm" variant="simple">
                    <Thead>
                      <Tr>
                        <Th
                          color="gray.400"
                          fontWeight={500}
                          textTransform="uppercase"
                          letterSpacing="0.08em"
                          fontSize="xs"
                          borderColor="gray.200"
                          whiteSpace="nowrap"
                        >
                          Label
                        </Th>
                        <Th
                          color="gray.400"
                          fontWeight={500}
                          textTransform="uppercase"
                          letterSpacing="0.08em"
                          fontSize="xs"
                          borderColor="gray.200"
                          whiteSpace="nowrap"
                        >
                          Prefix
                        </Th>
                        <Th
                          color="gray.400"
                          fontWeight={500}
                          textTransform="uppercase"
                          letterSpacing="0.08em"
                          fontSize="xs"
                          borderColor="gray.200"
                          whiteSpace="nowrap"
                        >
                          Status
                        </Th>
                        <Th
                          color="gray.400"
                          fontWeight={500}
                          textTransform="uppercase"
                          letterSpacing="0.08em"
                          fontSize="xs"
                          borderColor="gray.200"
                          whiteSpace="nowrap"
                        >
                          Created
                        </Th>
                        <Th
                          color="gray.400"
                          fontWeight={500}
                          textTransform="uppercase"
                          letterSpacing="0.08em"
                          fontSize="xs"
                          borderColor="gray.200"
                          whiteSpace="nowrap"
                        >
                          Expires
                        </Th>
                        <Th
                          color="gray.400"
                          fontWeight={500}
                          textTransform="uppercase"
                          letterSpacing="0.08em"
                          fontSize="xs"
                          borderColor="gray.200"
                          whiteSpace="nowrap"
                        >
                          Last used
                        </Th>
                        <Th
                          color="gray.400"
                          fontWeight={500}
                          textTransform="uppercase"
                          letterSpacing="0.08em"
                          fontSize="xs"
                          borderColor="gray.200"
                          whiteSpace="nowrap"
                        >
                          Created by
                        </Th>
                        <Th borderColor="gray.200" />
                      </Tr>
                    </Thead>
                    <Tbody>
                      {rows.map((r) => {
                        const isActive = r.status === 'active';
                        const isPending = pendingRevokeId === r.id;
                        return (
                          <Tr key={r.id}>
                            <Td borderColor="gray.200" color="gray.900">
                              {r.label}
                            </Td>
                            <Td
                              borderColor="gray.200"
                              color="gray.500"
                              sx={TABULAR}
                              fontFamily="mono"
                              fontSize="xs"
                            >
                              {r.tokenPrefix}
                            </Td>
                            <Td borderColor="gray.200">
                              <Text
                                as="span"
                                fontSize="xs"
                                fontWeight={500}
                                textTransform="uppercase"
                                letterSpacing="0.08em"
                                color={STATUS_COLOR[r.status]}
                              >
                                {r.status}
                              </Text>
                            </Td>
                            <Td
                              borderColor="gray.200"
                              color="gray.700"
                              sx={TABULAR}
                              whiteSpace="nowrap"
                            >
                              {fmtDate(r.createdAt)}
                            </Td>
                            <Td
                              borderColor="gray.200"
                              color="gray.700"
                              sx={TABULAR}
                              whiteSpace="nowrap"
                            >
                              {fmtDate(r.expiresAt)}
                            </Td>
                            <Td
                              borderColor="gray.200"
                              color="gray.700"
                              sx={TABULAR}
                              whiteSpace="nowrap"
                            >
                              {fmtDate(r.lastUsedAt)}
                            </Td>
                            <Td borderColor="gray.200" color="gray.700">
                              {r.createdBy
                                ? r.createdBy.name || r.createdBy.email
                                : '—'}
                            </Td>
                            <Td borderColor="gray.200" textAlign="right">
                              {isActive ? (
                                <AccessGate action="ADMIN_DASHBOARD_SHARE_REVOKE"><Button
                                  size="xs"
                                  variant={isPending ? 'solid' : 'ghost'}
                                  colorScheme={isPending ? 'red' : undefined}
                                  onClick={() => handleRevoke(r.id)}
                                >
                                  {isPending ? 'Confirm?' : 'Revoke'}
                                </Button></AccessGate>
                              ) : null}
                            </Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </Box>
              )}
            </Box>

            <Box
              borderTop="1px solid"
              borderColor="gray.200"
              pt={6}
            >
              {createdToken ? (
                <VStack align="stretch" spacing={3}>
                  <Text
                    fontSize="xs"
                    letterSpacing="0.2em"
                    textTransform="uppercase"
                    color="gray.400"
                    fontWeight={500}
                  >
                    New share link
                  </Text>
                  <Text fontSize="sm" color="gray.700">
                    «{createdToken.label}» — copy this URL now. The token is
                    shown only once and cannot be retrieved later.
                  </Text>
                  <Box
                    p={3}
                    bg="gray.50"
                    border="1px solid"
                    borderColor="gray.200"
                    fontFamily="mono"
                    fontSize="sm"
                    color="gray.900"
                    wordBreak="break-all"
                  >
                    {buildShareUrl(createdToken.token)}
                  </Box>
                  <HStack>
                    <Button size="sm" onClick={handleCopy}>
                      Copy URL
                    </Button>
                    <Spacer />
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setCreatedToken(null)}
                    >
                      Done
                    </Button>
                  </HStack>
                </VStack>
              ) : (
                <VStack align="stretch" spacing={3}>
                  <Text
                    fontSize="xs"
                    letterSpacing="0.2em"
                    textTransform="uppercase"
                    color="gray.400"
                    fontWeight={500}
                  >
                    New share link
                  </Text>
                  <HStack spacing={3} align="end" flexWrap="wrap">
                    <Box flex="1 1 240px">
                      <Text fontSize="xs" color="gray.500" mb={1}>
                        Label
                      </Text>
                      <input
                        value={label}
                        maxLength={120}
                        placeholder="e.g. Board chair — Anne"
                        onChange={(e) => setLabel(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.5rem 0.75rem',
                          border: '1px solid #E2E8F0',
                          fontSize: '0.875rem',
                          color: '#1A202C',
                          background: 'white',
                          outline: 'none',
                        }}
                      />
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500" mb={1}>
                        Expires
                      </Text>
                      <DatePicker
                        selected={expiry}
                        onChange={(d) => setExpiry(d)}
                        minDate={tomorrow}
                        maxDate={max}
                        dateFormat="dd MMM yyyy"
                        placeholderText="Pick a date"
                        className="companies-stats-datepicker"
                      />
                    </Box>
                    <Box>
                      <AccessGate action="ADMIN_DASHBOARD_SHARE_CREATE"><Button
                        size="sm"
                        colorScheme="blue"
                        isLoading={isCreating}
                        isDisabled={!canSubmit}
                        onClick={handleCreate}
                      >
                        Create
                      </Button></AccessGate>
                    </Box>
                  </HStack>
                  {!expiryValid && expiry !== null && (
                    <Text fontSize="xs" color="red.500">
                      Pick a date between tomorrow and 5 years from now.
                    </Text>
                  )}
                </VStack>
              )}
            </Box>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default CompaniesStatsShareModal;
